import utils from '../utils';

export interface Coordinates {
  x: number;
  y: number;
  touches?: Coordinates[];
}

type EventName = 'touchStart' | 'touchEnd' | 'drag' | 'hover' | 'mouseOut' | 'dragOut';
const svgNS = 'http://www.w3.org/2000/svg';

const isDragSymbol = Symbol('dragging');
const isOutSymbol = Symbol('isOut');
const windowListeners = Symbol('windowListeners');
const listeners = Symbol('listener');

const debug = false;
const log = debug ? console.log.bind(console) : () => {};

const preventDefault = (e) => {
  if (
    typeof window !== 'undefined' &&
    typeof TouchEvent !== 'undefined' &&
    // eslint-disable-next-line no-undef
    e instanceof TouchEvent
  ) {
    e.preventDefault();
  }
};

/**
 * A class to enable touch & mouse interactions on SVGs. Translates browser coordinates to
 * SVG coordinates.
 *
 * Call SVGInteraction.makeInteractive(svg), then override this class's methods to use.
 */
export default class SVGInteraction {
  svg: SVGSVGElement;

  svgPt: SVGPoint;

  isInteractive = false;

  [listeners]: [EventName, Function][];

  overlayDiv: HTMLDivElement;

  private label: HTMLLabelElement | false = false;

  private title: SVGTitleElement | false = false;

  constructor(svg, svgPt?: SVGPoint) {
    this.svg = svg;
    if (svgPt && !(svgPt instanceof window.SVGPoint)) {
      throw new Error('svgPt argument must be of type SVGPoint');
    }
    this.svgPt = svgPt;
  }

  /**
   * Update only the label.
   * @param labelOrString
   */
  setLabel(labelOrString: HTMLLabelElement | string) {
    if (labelOrString instanceof HTMLLabelElement) {
      this.createLabel(labelOrString);
      return;
    }
    if (!this.label) this.createLabel();
    if (this.label) this.label.innerHTML = labelOrString;
  }

  /**
   * Update the <label>, and also the <title> if a string is passed
   * @param innerHTML
   */
  setTitle(innerHTML: string | Node | HTMLElement) {
    if (!this.label) this.createLabel();
    if (!this.label) return;
    if (utils.isString(innerHTML)) {
      this.createTitle();
      if (this.title) this.title.textContent = innerHTML;
      return;
    }
    this.deleteTitle();
    this.label.innerHTML = '';
    this.label.append(innerHTML);
  }

  private createTitle() {
    if (this.title) return;
    const title = document.createElementNS(svgNS, 'title');
    this.svg.appendChild(title);
    this.title = title;
  }

  deleteTitle() {
    if (!this.title) return;
    this.svg.removeChild(this.title);
    this.title = false;
  }

  private createLabel(label?: HTMLLabelElement) {
    console.log('creating label div');
    if (!this.svg) return;
    const parent = this.svg.parentElement;
    if (!parent) return;

    label = label || document.createElement('label');
    label.classList.add('sr-only');
    const uuid = utils.uuid;
    const labelId = label.getAttribute('id') || `label-for-svg-element-${uuid}`;
    label.setAttribute('id', labelId);
    this.svg.setAttribute('aria-labelledby', labelId);
    const svgId = this.svg.getAttribute('id');
    if (!svgId) {
      this.svg.setAttribute('id', `svg-element-${uuid}`);
    }
    label.setAttribute('for', this.svg.getAttribute('id'));

    const firstChild = parent.firstChild;
    parent.insertBefore(label, firstChild);

    this.label = label;
  }

  deleteLabel() {
    if (!this.label) return;
    this.svg.parentElement.removeChild(this.label);
    this.svg.removeAttribute('aria-labelledby');
  }

  /* eslint-disable no-unused-vars */
  // These are here as holders -- if you extend this class, implement them.
  touchStart(e: TouchEvent | MouseEvent, coords: Coordinates) {
    preventDefault(e);
    log('touchStart', coords);
    this.callListeners('touchStart', e, coords);
  }

  touchEnd(e: TouchEvent | MouseEvent, coords: Coordinates) {
    preventDefault(e);
    log('touchEnd', coords);
    this.callListeners('touchEnd', e, coords);
  }

  drag(e: TouchEvent | MouseEvent, coords: Coordinates) {
    preventDefault(e);
    log('drag', coords);
    this.callListeners('drag', e, coords);
  }

  hover(e: TouchEvent | MouseEvent, coords: Coordinates) {
    preventDefault(e);
    log('hover', coords);
    this.callListeners('hover', e, coords);
  }

  mouseOut(e: TouchEvent | MouseEvent, coords: Coordinates) {
    log('mouseOut', coords);
    this.callListeners('mouseOut', e, coords);
  }

  dragOut(e: TouchEvent | MouseEvent, coords: Coordinates) {
    log('dragOut', coords);
    this.callListeners('dragOut', e, coords);
  }

  addEventListener(type: EventName, callback: Function) {
    this[listeners].push([type, callback]);
  }

  removeEventListener(type: EventName, callback: Function) {
    const index = this[listeners].findIndex(
      ([listenerType, listenerCallback]) => type === listenerType && callback === listenerCallback
    );
    if (index !== -1) this[listeners].splice(index, 1);
    return index !== -1;
  }

  callListeners(type: EventName, e: MouseEvent | KeyboardEvent | TouchEvent, coords: Coordinates) {
    this[listeners].forEach(([listenerType, callback]) => {
      if (type === listenerType) {
        callback(e, coords);
      }
    });
  }

  makeInteractive(svg = this.svg) {
    if (this.isInteractive) throw new Error(`${this} is already interactive.`);
    this.isInteractive = true;
    this[listeners] = [];
    // We move the svg inside a relatively positioned div, filling its
    // parent. We add an overlay absolutely positioned div filling its parent
    // aver the svg and catch events on that.

    // The svg itself cannot be a target of events, though events may bubble
    // up to it: https://www.w3.org/TR/SVG/interact.html#hit-testing
    // so in editor/renderer we discreetly paint a see-through box over
    // top of the full svg that serves as the event target.
    const parent = svg.parentElement;
    parent.removeChild(svg);
    utils.merge(parent.style, {
      display: 'flex',
      'flex-direction': 'column',
    });

    const containerDiv = document.createElement('div');
    utils.merge(containerDiv.style, {
      position: 'relative',
      border: 'none',
      margin: 'none',
      padding: 'none',
      width: '100%',
      'flex-grow': '1',
    });
    containerDiv.appendChild(svg);
    const overlayDiv = document.createElement('div');
    this.overlayDiv = overlayDiv;
    utils.merge(overlayDiv.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      // 'flex-grow': '1',
      opacity: '0',
    });
    containerDiv.appendChild(overlayDiv);
    parent.appendChild(containerDiv);
    // An SVG point is used to translate div space to SVG space. See Alexander Jank's solution at:
    // https://stackoverflow.com/questions/29261304/how-to-get-the-click-coordinates-relative-to-svg-element-holding-the-onclick-lis
    this.svgPt = this.svgPt || svg.createSVGPoint();

    /** ==== Condition Testing & Setting Functions ====
     *
     * These functions are used to translate UI events from the browser into more helpful
     * events from a programatic perspective. They take care of tracking mousestate &
     * touchstate (down or up) to fire drag vs. hover. If a user drags outside of the SVG,
     * they continue to fire the drag event, so that (if desired) things like drag & drop
     * among elements can be implemented.
     *
     */

    // A  not-combinator
    const not = (condition) => () => !condition();
    // Get whether we're dragging
    const isDragging = () => this[isDragSymbol];
    // Set whether the mouse is down or up.
    const down = () => {
      this[isDragSymbol] = true;
      return true;
    };
    const up = () => {
      this[isDragSymbol] = false;
      return true;
    };

    // Get whether we're outside of the SVG bounds
    const isOutside = () => this[isOutSymbol];
    // Set whether we're in our out; if we move out while dragging we need window listeners briefly.
    const inside = () => {
      this[isOutSymbol] = false;
      this[windowListeners].forEach(([eventType, listener]) => {
        window.removeEventListener(eventType, listener, false);
      });
      return true;
    };
    const outside = () => {
      this[isOutSymbol] = true;
      this[windowListeners].forEach(([eventType, listener]) => {
        window.addEventListener(eventType, listener);
      });
      return true;
    };

    // We'll hold the window listeners here so we can add & remove them as needed.
    this[windowListeners] = [];

    // Utility function to check event conditions & fire class events if needed
    const addListener = ([eventType, callback, ifTrue, el]: [
      string,
      Function,
      () => boolean,
      any
    ]) => {
      el = el || svg.nextSibling;
      const listener = (evt) => {
        log(evt.type);
        if (ifTrue()) {
          const coords = getCoords(evt, svg, this.svgPt);
          if (eventType === 'touchstart') {
            console.log('gotCoords', coords);
          }
          callback.call(this, evt, coords);
        }
      };

      if (el !== window) el.addEventListener(eventType, listener);
      else this[windowListeners].push([eventType, listener]);
    };

    // [ type, listener, testFunction (fire listener if true), EventTarget (default this.svg)]
    [
      /* events occuring within SVG */
      // Touch is started
      ['mousedown', this.touchStart, down],
      // Touch is started
      ['touchstart', this.touchStart, down],
      // Touch ends or mouse up
      ['mouseup', this.touchEnd, up],
      // Touch ends or mouse up
      ['touchend', this.touchEnd, up],
      // Dragging
      ['touchmove', this.drag, isDragging],
      // Dragging
      ['mousemove', this.drag, isDragging],
      // Hover
      ['mousemove', this.hover, not(isDragging)],
      // Mouseout
      ['mouseout', this.mouseOut, not(isDragging)],
      // Mouseout (touch interrupt)
      ['touchcancel', this.mouseOut, not(isDragging)],
      // goes out of bounds isDown & set
      ['mouseout', this.dragOut, () => isDragging() && outside()],
      // goes out of bounds isDown & set
      ['touchcancel', this.dragOut, () => isDragging() && outside()],
      // comes back inside
      ['touchmove', inside, isOutside],
      // comes back inside
      ['mousemove', inside, isOutside],
      // comes back inside, this shouldn't happen, but just in case
      ['mousedown', inside, isOutside],
      // comes back inside, this shouldn't happen, but just in case
      ['touchstart', inside, isOutside],

      /* out of bounds events */
      // if outside in window & dragging
      ['touchmove', this.drag, () => isOutside() && isDragging(), window],
      // if outside in window & dragging
      ['mousemove', this.drag, () => isOutside() && isDragging(), window],
      // if outside in window & dragging & released
      ['mouseup', this.touchEnd, () => isOutside() && isDragging() && up() && inside(), window],
      // if outside in window & dragging & released
      ['touchend', this.touchEnd, () => isOutside() && isDragging() && up() && inside(), window],
    ].forEach(addListener);
  }
}

/**
 * Returns coordinates for an event
 * @param {Event} e touch or mouse event
 * @param {SVGPoint} svgPt an SVG point
 * @param {SVGElement} svg the SVG's client bounding rectangle
 *
 * @returns {Object} { x, y, [touches] }
 */
function getCoords(
  e: TouchEvent | MouseEvent | Touch,
  svg: SVGSVGElement,
  svgPt: SVGPoint
): Coordinates {
  if ('changedTouches' in e) {
    const length = e.changedTouches.length;
    const touches: Coordinates[] = [];
    for (let i = 0; i < length; i++) {
      touches.push(getCoords(e.changedTouches.item(i), svg, svgPt));
    }
    return { x: touches[0].x, y: touches[0].y, touches };
  }

  svgPt.x = e.clientX;
  svgPt.y = e.clientY;
  const svgCoords = svgPt.matrixTransform(svg.getScreenCTM().inverse());
  return { x: svgCoords.x, y: svgCoords.y };
}

export function toClientCoords(
  svg: SVGSVGElement,
  { x, y }: { x: number; y: number }
): { x: number; y: number } {
  const svgPoint = svg.createSVGPoint();
  svgPoint.x = x;
  svgPoint.y = y;
  return svgPoint.matrixTransform(svg.getScreenCTM());
}

export function toSVGCoords(
  svg: SVGSVGElement,
  { x, y }: { x: number; y: number }
): { x: number; y: number } {
  const svgPoint = svg.createSVGPoint();
  svgPoint.x = x;
  svgPoint.y = y;
  return svgPoint.matrixTransform(svg.getScreenCTM().inverse());
}
